// ✅ src/pages/VehicleV2/TyreMaintenanceCostReport.jsx
// UPDATED — Tyre Life Detail data + Production listing template

import React, {
  useEffect,
  useMemo,
  useState,
  useContext,
  useRef,
  useCallback,
} from "react";
import axios from "axios";
import {
  FaSearch,
  FaPrint,
  FaArrowLeft,
  FaFileExport,
  FaSyncAlt,
  FaChevronDown,
  FaChevronUp,
  FaEye,
} from "react-icons/fa";

import HeaderBar from "../../components/HeaderBar";
import VehicleLayoutWrapper from "../../components/VehicleLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate } from "react-router-dom";

const API_BASE = "https://nissi-india.com/api/tyreLifeDetailsReport";

const toInt = (v, d = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? Math.trunc(n) : d;
};

const toStr = (v, def = "") =>
  v === null || v === undefined ? def : String(v).trim();

const toList = (arr) => (Array.isArray(arr) ? arr : []);

const toNum = (v, d = 0) => {
  if (v === null || v === undefined || v === "") return d;
  const n = Number(String(v).replace(/,/g, ""));
  return Number.isFinite(n) ? n : d;
};

const createDefaultFilters = (branchType, branchId) => ({
  branchId: branchType === "HO" ? "Select" : String(branchId),
  fromDate: "",
  toDate: "",
  tyreSerialNo: "",
  vehicleName: "",
  vehicleRegNo: "",
  vehicleCode: "",
  tyreModel: "",
  tyreMake: "Select",
  tyreSize: "",
  status: "Select",
});

const exportToCsv = (rows, filename = "tyre-life-details.csv") => {
  if (!rows?.length) return;
  const headers = Object.keys(rows[0]);
  const csv = [
    headers.join(","),
    ...rows.map((r) =>
      headers
        .map((h) => `"${String(r[h] ?? "").replace(/"/g, '""')}"`)
        .join(",")
    ),
  ].join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

const mapDbRowToUi = (r, idx) => ({
  id: r.fld_tyreid ?? r.fld_addtyre_id ?? idx + 1,
  tyreSerialNo: r.fld_tyreserailno ?? r.fld_tyre_serial_no ?? "",
  tyreMake: r.fld_tyremake ?? r.fld_tyre_make ?? "",
  tyreModel: r.fld_tyremodel ?? r.fld_tyre_model ?? "",
  vehicleCode: r.fld_vehicle_code ?? "",
  vehicleNo: r.fld_vehicleregno ?? r.fld_vehicle_no ?? "",
  vehicleName: r.fld_vehicle_name ?? "",
  originalRtd: r.fld_status ?? r.fld_original_rtd ?? "",
  fittingDate: r.fld_fittingdate ?? r.fld_fitting_date ?? "",
  removingDate: r.fld_removingdate ?? r.fld_removing_date ?? "",
  fittingHrKm: toNum(r.fld_fittingkmhr ?? r.fld_fitting_hr_km, 0),
  removingHrKm: toNum(r.fld_RemovingKmhr ?? r.fld_removingkmhr ?? r.fld_removing_hr_km, 0),
  runningHrKm: toNum(r.fld_runninghrkm ?? r.fld_running_hr_km, 0),
  costOfTyre: toNum(r.fld_tyrerate ?? r.fld_cost_of_tyre, 0),
  costHrKm: toNum(r.fld_tyrecost ?? r.fld_cost_hr_km, 0),
  branchName: r.fld_branchname ?? "",
  tyreSize: r.fld_tyresize ?? r.fld_tyre_size ?? "",
  status: r.fld_tyrestatus ?? r.fld_currentstatus ?? r.fld_useoftyre ?? "",
  raw: r,
});

const TyreMaintenanceCostReport = () => {
  const navigate = useNavigate();
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;
  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;
  const tableHeaderClass = buttonClass;

  const finYearId = localStorage.getItem("finYearId") || "";
  const finYear = localStorage.getItem("finYear") || "";
  const branchIdRaw =
    localStorage.getItem("branchId") ||
    localStorage.getItem("Branchid") ||
    localStorage.getItem("branchid") ||
    "8";
  const branchType =
    localStorage.getItem("branchType") ||
    localStorage.getItem("branchtype") ||
    "HO";
  const userId =
    localStorage.getItem("userId") ||
    localStorage.getItem("loginuserid") ||
    localStorage.getItem("loginUserId") ||
    "0";
  const loginUserId = sessionStorage.getItem("loginUserId") || userId || "0";
  const sessionBranchId = toInt(branchIdRaw, 8);

  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": String(sessionBranchId),
      "x-branch": String(branchType),
      "x-userid": String(loginUserId),
    }),
    [finYearId, finYear, sessionBranchId, branchType, loginUserId]
  );

  const session = useMemo(
    () => ({
      branchId: sessionBranchId,
      branchType: toStr(branchType || "HO"),
      loginUserId: toInt(loginUserId, 0),
    }),
    [sessionBranchId, branchType, loginUserId]
  );

  const defaultFilters = useMemo(
    () => createDefaultFilters(session.branchType, session.branchId),
    [session.branchType, session.branchId]
  );

  const didInitRef = useRef(false);
  const inFlightRef = useRef(false);
  const [pageLoading, setPageLoading] = useState(false);
  const [listLoading, setListLoading] = useState(false);
  const isBusy = pageLoading || listLoading;
  const [showFilters, setShowFilters] = useState(true);
  const [filters, setFilters] = useState(defaultFilters);
  const [allRows, setAllRows] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const fieldClass = useMemo(
    () =>
      `w-full max-w-full min-w-0 box-border px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
        isDark
          ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
          : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
      }`,
    [isDark]
  );

  const labelClass = useMemo(
    () => `block mb-2 text-sm font-medium ${isDark ? "text-gray-300" : "text-gray-700"}`,
    [isDark]
  );

  const tdClass = useMemo(
    () => `px-4 py-3 text-sm ${isDark ? "text-gray-300" : "text-gray-900"}`,
    [isDark]
  );

  const total = allRows.length;
  const totalPages = useMemo(() => Math.max(1, Math.ceil(total / itemsPerPage)), [total]);
  const indexOfFirstItem = (currentPage - 1) * itemsPerPage;
  const indexOfLastItem = Math.min(currentPage * itemsPerPage, total);
  const pageRows = useMemo(
    () => allRows.slice(indexOfFirstItem, indexOfFirstItem + itemsPerPage),
    [allRows, indexOfFirstItem]
  );

  const lastOccurrenceMap = useMemo(() => {
    const map = new Map();
    const tyreGroups = new Map();
    pageRows.forEach((row, idx) => {
      const tyreSerialNo = toStr(row.tyreSerialNo);
      if (!tyreSerialNo) return;
      if (!tyreGroups.has(tyreSerialNo)) tyreGroups.set(tyreSerialNo, []);
      tyreGroups.get(tyreSerialNo).push({ index: idx, row });
    });
    tyreGroups.forEach((items, tyreSerialNo) => {
      const lastItem = items[items.length - 1];
      map.set(lastItem.index, {
        isLast: true,
        tyreSerialNo,
        tyreId: lastItem.row.id,
        groupSize: items.length,
      });
    });
    return map;
  }, [pageRows]);

  const setField = useCallback((name, value) => {
    setFilters((prev) => ({ ...prev, [name]: value }));
  }, []);

  const handleChange = useCallback((e) => {
    setField(e.target.name, e.target.value);
  }, [setField]);

  const fetchList = useCallback(async (overrideFilters = filters) => {
    if (inFlightRef.current) return;
    try {
      inFlightRef.current = true;
      setListLoading(true);
      const f = overrideFilters || filters;
      const params = {
        branchId: session.branchType === "HO" ? f.branchId || "Select" : String(session.branchId),
        fromDate: f.fromDate || "",
        toDate: f.toDate || "",
        tyreSerialNo: f.tyreSerialNo || "",
        vehicleName: f.vehicleName || "",
        vehicleRegNo: f.vehicleRegNo || "",
        vehicleCode: f.vehicleCode || "",
        tyreModel: f.tyreModel || "",
        tyreMake: f.tyreMake || "Select",
        tyreSize: f.tyreSize || "",
        status: f.status || "Select",
        page: 1,
        pageSize: 1000000,
      };
      const res = await axios.get(`${API_BASE}/list`, { params, headers: commonHeaders });
      if (res.data?.success === false) {
        toast.error(res.data?.message || "Failed to load tyre life report");
        setAllRows([]);
        setCurrentPage(1);
        return;
      }
      setAllRows(toList(res.data?.rows).map(mapDbRowToUi));
      setCurrentPage(1);
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || err.message || "API error");
      setAllRows([]);
      setCurrentPage(1);
    } finally {
      setListLoading(false);
      inFlightRef.current = false;
    }
  }, [filters, commonHeaders, session.branchType, session.branchId]);

  const loadPage = useCallback(async () => {
    try {
      setPageLoading(true);
      setFilters(defaultFilters);
      setAllRows([]);
      setCurrentPage(1);
      await fetchList(defaultFilters);
    } finally {
      setPageLoading(false);
    }
  }, [defaultFilters, fetchList]);

  const handleSearch = useCallback(async () => {
    await fetchList(filters);
    toast.success("Report loaded");
  }, [fetchList, filters]);

  const handleViewAll = useCallback(async () => {
    const reset = createDefaultFilters(session.branchType, session.branchId);
    setFilters(reset);
    await fetchList(reset);
    toast.info("Showing all records");
  }, [session.branchType, session.branchId, fetchList]);

  const handleClearFilters = useCallback(() => {
    setFilters(createDefaultFilters(session.branchType, session.branchId));
    toast.info("Filters cleared. Click Search to apply.");
  }, [session.branchType, session.branchId]);

  const handleRefresh = useCallback(async () => {
    await loadPage();
    toast.info("Page refreshed");
  }, [loadPage]);

  const handleBack = useCallback(() => navigate(-1), [navigate]);

  const paginate = useCallback((pageNumber) => {
    setCurrentPage(Math.min(Math.max(1, pageNumber), totalPages));
  }, [totalPages]);

  const handleTyrePrint = useCallback((item, groupInfo) => {
    const tyreSerialNo = toStr(item.tyreSerialNo);
    const tyreId = toStr(item.id);
    if (!tyreSerialNo) return toast.error("Tyre Serial No missing");
    if (!tyreId) return toast.error("Tyre Id missing");
    toast.info(`Printing ${groupInfo.groupSize} record(s) for ${tyreSerialNo}`);
    navigate("/vehicle/TyreCostReportPage", {
      state: {
        branchType: session.branchType,
        loginUserId: String(session.loginUserId),
        sessionBranchId: Number(session.branchId),
        tyreSerialNo,
        tyreId,
        branchId: filters.branchId,
        fromDate: filters.fromDate,
        toDate: filters.toDate,
        vehicleName: filters.vehicleName,
        vehicleRegNo: filters.vehicleRegNo,
        vehicleCode: filters.vehicleCode,
        tyreModel: filters.tyreModel,
        tyreMake: filters.tyreMake,
        tyreSize: filters.tyreSize,
        status: filters.status,
        pageSize: 1000,
      },
    });
  }, [navigate, session, filters]);

  const handleExport = useCallback(() => {
    try {
      if (!allRows.length) return toast.info("No data to export");
      exportToCsv(allRows.map((r) => ({
        tyreSerialNo: r.tyreSerialNo,
        tyreMake: r.tyreMake,
        tyreModel: r.tyreModel,
        vehicleCode: r.vehicleCode,
        vehicleNo: r.vehicleNo,
        vehicleName: r.vehicleName,
        originalRtd: r.originalRtd,
        fittingDate: r.fittingDate,
        removingDate: r.removingDate,
        fittingHrKm: r.fittingHrKm,
        removingHrKm: r.removingHrKm,
        runningHrKm: r.runningHrKm,
        costOfTyre: r.costOfTyre,
        costHrKm: r.costHrKm,
        branchName: r.branchName,
        tyreSize: r.tyreSize,
        status: r.status,
      })), "tyre-maintenance-cost-report.csv");
      toast.success("Exported CSV");
    } catch (err) {
      console.error(err);
      toast.error(err.message || "Export failed");
    }
  }, [allRows]);

  const handleAllPrint = useCallback(() => {
    toast.info("Preparing print report...");
    navigate("/vehicle/TyreCostReportPage", {
      state: {
        branchType: session.branchType,
        loginUserId: String(session.loginUserId),
        sessionBranchId: Number(session.branchId),
        branchId: filters.branchId,
        fromDate: filters.fromDate,
        toDate: filters.toDate,
        tyreSerialNo: filters.tyreSerialNo,
        vehicleName: filters.vehicleName,
        vehicleRegNo: filters.vehicleRegNo,
        vehicleCode: filters.vehicleCode,
        tyreModel: filters.tyreModel,
        tyreMake: filters.tyreMake,
        tyreSize: filters.tyreSize,
        status: filters.status,
        currentPage,
        currentPageRecordCount: pageRows.length,
        totalRecords: allRows.length,
        pageSize: itemsPerPage,
      },
    });
  }, [navigate, session, filters, currentPage, pageRows.length, allRows.length]);

  useEffect(() => {
    if (didInitRef.current) return;
    didInitRef.current = true;
    (async () => { await loadPage(); })();
  }, [loadPage]);

  const statusText = pageLoading ? "Loading page..." : listLoading ? "Searching..." : "Ready";

  return (
    <VehicleLayoutWrapper>
      <div className={`w-full max-w-full min-w-0 overflow-x-hidden box-border min-h-screen p-4 md:p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-2xl md:text-3xl">TYRE MAINTENANCE COST REPORT</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div className={`w-full max-w-full min-w-0 overflow-hidden p-4 md:p-6 mt-6 rounded-2xl shadow-xl border box-border ${
          isDark
            ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
            : "bg-white text-black"
        }`}>
          <div className="flex justify-between items-center mb-6 flex-wrap gap-3">
            <div className="min-w-0">
              <h2 className={`text-xl md:text-2xl font-bold break-words ${isDark ? "text-white" : "text-gray-900"}`}>
                Tyre Maintenance Cost Report
              </h2>
              <p className={`text-sm mt-1 ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                Branch ID: {session.branchId || "-"} • User ID: {session.loginUserId || "-"}
              </p>
            </div>
            <div className="flex items-center gap-2 md:gap-3 flex-wrap">
              <button onClick={handleAllPrint} disabled={isBusy || pageRows.length === 0} type="button" className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-200 ${buttonClass} ${hoverClass} text-white ${isBusy || pageRows.length === 0 ? "opacity-60 cursor-not-allowed" : ""}`}>
                <FaPrint /> Print
              </button>
              <button onClick={handleExport} disabled={isBusy || allRows.length === 0} type="button" className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform transition duration-200 ${isDark ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600" : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"} ${isBusy || allRows.length === 0 ? "opacity-60 cursor-not-allowed" : ""}`}>
                <FaFileExport /> Export
              </button>
              <button onClick={handleRefresh} disabled={isBusy} type="button" className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform transition duration-200 ${isDark ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600" : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"} ${isBusy ? "opacity-60 cursor-not-allowed" : ""}`}>
                <FaSyncAlt /> Refresh
              </button>
              <button onClick={handleBack} disabled={isBusy} type="button" className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform transition duration-200 ${isDark ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600" : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"} ${isBusy ? "opacity-60 cursor-not-allowed" : ""}`}>
                <FaArrowLeft /> Back
              </button>
              <button onClick={() => setShowFilters((p) => !p)} type="button" className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform transition duration-200 ${isDark ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600" : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"}`}>
                {showFilters ? <FaChevronUp /> : <FaChevronDown />}
                {showFilters ? "Hide Filters" : "Show Filters"}
              </button>
            </div>
          </div>

          {showFilters && (
            <div className={`p-4 md:p-6 rounded-lg mb-6 overflow-hidden ${isDark ? "bg-gray-800/50" : "bg-blue-50"}`}>
              <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
                <h3 className={`text-lg font-semibold ${isDark ? "text-white" : "text-blue-900"}`}>Search Criteria</h3>
                <div className="flex gap-2 flex-wrap">
                  <button onClick={handleClearFilters} type="button" disabled={isBusy} className={`px-4 py-2 rounded-lg text-sm font-semibold border ${isDark ? "border-gray-600 text-gray-200 hover:bg-gray-700" : "border-gray-300 text-gray-700 hover:bg-white"} ${isBusy ? "opacity-60 cursor-not-allowed" : ""}`}>Clear</button>
                  <button onClick={handleViewAll} type="button" disabled={isBusy} className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold border ${isDark ? "border-gray-600 text-gray-200 hover:bg-gray-700" : "border-gray-300 text-gray-700 hover:bg-white"} ${isBusy ? "opacity-60 cursor-not-allowed" : ""}`}><FaEye /> View All</button>
                  <button onClick={handleSearch} type="button" disabled={isBusy} className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${isBusy ? "opacity-60 cursor-not-allowed" : ""} ${buttonClass} ${hoverClass} text-white`}><FaSearch /> {listLoading ? "Searching..." : "Search"}</button>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                <div className="min-w-0">
                  <label className={labelClass}>Branch</label>
                  <select name="branchId" value={filters.branchId} onChange={handleChange} className={fieldClass} disabled={isBusy || session.branchType !== "HO"}>
                    <option value="Select">Select</option>
                    <option value={String(session.branchId)}>Branch {session.branchId}</option>
                    <option value="HILARA">HILARA</option>
                  </select>
                </div>
                <div className="min-w-0">
                  <label className={labelClass}>From Date</label>
                  <input type="date" name="fromDate" value={filters.fromDate} onChange={handleChange} className={fieldClass} disabled={isBusy} />
                </div>
                <div className="min-w-0">
                  <label className={labelClass}>To Date</label>
                  <input type="date" name="toDate" value={filters.toDate} onChange={handleChange} className={fieldClass} disabled={isBusy} />
                </div>
                <div className="min-w-0">
                  <label className={labelClass}>Tyre Serial No</label>
                  <input type="text" name="tyreSerialNo" value={filters.tyreSerialNo} onChange={handleChange} className={fieldClass} disabled={isBusy} placeholder="Enter tyre serial no" />
                </div>
                <div className="min-w-0">
                  <label className={labelClass}>Vehicle Name</label>
                  <input type="text" name="vehicleName" value={filters.vehicleName} onChange={handleChange} className={fieldClass} disabled={isBusy} placeholder="Enter vehicle name" />
                </div>
                <div className="min-w-0">
                  <label className={labelClass}>Vehicle Reg No</label>
                  <input type="text" name="vehicleRegNo" value={filters.vehicleRegNo} onChange={handleChange} className={fieldClass} disabled={isBusy} placeholder="Enter registration number" />
                </div>
                <div className="min-w-0">
                  <label className={labelClass}>Vehicle Code</label>
                  <input type="text" name="vehicleCode" value={filters.vehicleCode} onChange={handleChange} className={fieldClass} disabled={isBusy} placeholder="Enter vehicle code" />
                </div>
                <div className="min-w-0">
                  <label className={labelClass}>Tyre Model</label>
                  <input type="text" name="tyreModel" value={filters.tyreModel} onChange={handleChange} className={fieldClass} disabled={isBusy} placeholder="Enter tyre model" />
                </div>
                <div className="min-w-0">
                  <label className={labelClass}>Tyre Make</label>
                  <select name="tyreMake" value={filters.tyreMake} onChange={handleChange} className={fieldClass} disabled={isBusy}>
                    <option value="Select">Select</option>
                    <option value="APOLLO">APOLLO</option>
                    <option value="MRF">MRF</option>
                    <option value="CEAT">CEAT</option>
                    <option value="JK TYRES">JK TYRES</option>
                    <option value="BIRLA">BIRLA</option>
                  </select>
                </div>
                <div className="min-w-0">
                  <label className={labelClass}>Tyre Size</label>
                  <input type="text" name="tyreSize" value={filters.tyreSize} onChange={handleChange} className={fieldClass} disabled={isBusy} placeholder="Enter tyre size" />
                </div>
                <div className="min-w-0">
                  <label className={labelClass}>Status</label>
                  <select name="status" value={filters.status} onChange={handleChange} className={fieldClass} disabled={isBusy}>
                    <option value="Select">Select</option>
                    <option value="Active">Active</option>
                    <option value="Removed">Removed</option>
                  </select>
                </div>
              </div>
            </div>
          )}

          <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
            <div className={`px-3 py-1 rounded-full text-sm font-medium ${isDark ? "bg-blue-900 text-blue-100" : "bg-blue-100 text-blue-800"}`}>Total Records: {total}</div>
            <div className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>{statusText}</div>
          </div>

          <div className="w-full max-w-full overflow-x-auto rounded-lg border shadow-lg">
            {pageLoading || listLoading ? (
              <div className={`p-16 text-center ${isDark ? "bg-gray-800" : "bg-gray-50"}`}>
                <div className={`text-lg font-medium ${isDark ? "text-gray-300" : "text-gray-600"}`}>Loading...</div>
              </div>
            ) : pageRows.length === 0 ? (
              <div className={`p-16 text-center ${isDark ? "bg-gray-800" : "bg-gray-50"}`}>
                <div className={`text-lg font-medium ${isDark ? "text-gray-400" : "text-gray-500"}`}>No Tyre Records Found</div>
                <p className={`mt-2 text-sm ${isDark ? "text-gray-500" : "text-gray-400"}`}>Try adjusting your filters or click View All.</p>
              </div>
            ) : (
              <table className={`w-full ${isDark ? "bg-gray-800" : "bg-white"}`}>
                <thead className={`${tableHeaderClass} text-white`}>
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-semibold">S.No</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold">Tyre Serial No</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold">Tyre Make</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold">Vehicle No</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold">Fitting Date</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold">Running HR/KM</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold">Cost Of Tyre</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold">Cost HR/KM</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold">Status</th>
                    <th className="px-4 py-3 text-center text-sm font-semibold">Print</th>
                  </tr>
                </thead>
                <tbody className={`${isDark ? "divide-gray-700" : "divide-gray-200"} divide-y`}>
                  {pageRows.map((item, idx) => {
                    const groupInfo = lastOccurrenceMap.get(idx);
                    const showPrintButton = groupInfo?.isLast;
                    return (
                      <tr key={`${item.id || idx}-${idx}`} className={`${idx % 2 === 0 ? (isDark ? "bg-gray-800" : "bg-white") : (isDark ? "bg-gray-750" : "bg-gray-50")} transition-colors ${isDark ? "hover:bg-gray-700" : "hover:bg-blue-50"}`}>
                        <td className={tdClass}>{indexOfFirstItem + idx + 1}</td>
                        <td className={`px-4 py-3 text-sm font-medium ${isDark ? "text-blue-400" : "text-blue-600"}`}>{item.tyreSerialNo || "-"}</td>
                        <td className={tdClass}>{item.tyreMake || "-"}</td>
                        <td className={`px-4 py-3 text-sm font-medium ${isDark ? "text-white" : "text-gray-900"}`}>{item.vehicleNo || "-"}</td>
                        <td className={tdClass}>{item.fittingDate || "N/A"}</td>
                        <td className={tdClass}>{item.runningHrKm ?? "N/A"}</td>
                        <td className={`px-4 py-3 text-sm font-semibold ${isDark ? "text-green-400" : "text-green-600"}`}>₹{Number(item.costOfTyre || 0).toLocaleString("en-IN")}</td>
                        <td className={`px-4 py-3 text-sm font-semibold ${isDark ? "text-green-400" : "text-green-600"}`}>₹{Number(item.costHrKm || 0).toFixed(2)}</td>
                        <td className={`px-4 py-3 text-sm font-semibold ${isDark ? "text-white" : "text-gray-900"}`}>{item.status || "N/A"}</td>
                        <td className="px-4 py-3 text-center">
                          {showPrintButton ? (
                            <button onClick={() => handleTyrePrint(item, groupInfo)} disabled={isBusy} className={`p-2 rounded transition ${isDark ? "text-white hover:bg-gray-600" : "text-gray-800 hover:bg-gray-200"} ${isBusy ? "opacity-60 cursor-not-allowed" : ""}`} title={`Print ${groupInfo.groupSize} record(s) for ${groupInfo.tyreSerialNo}`} type="button">
                              <FaPrint className="w-4 h-4" />
                            </button>
                          ) : (
                            <span className="text-gray-400 text-xs">-</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>

          {total > 0 && (
            <div className="mt-6 flex flex-col sm:flex-row justify-between items-center gap-3">
              <div className={`text-sm ${isDark ? "text-gray-400" : "text-gray-600"}`}>
                Showing {total === 0 ? 0 : indexOfFirstItem + 1} to {indexOfLastItem} of {total} entries
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <button onClick={() => paginate(currentPage - 1)} disabled={currentPage === 1 || isBusy} type="button" className={`px-4 py-2 border rounded-lg font-semibold ${isDark ? "border-gray-600 text-gray-300 hover:bg-gray-700" : "border-gray-300 text-gray-700 hover:bg-gray-100"} ${currentPage === 1 || isBusy ? "opacity-50 cursor-not-allowed" : ""}`}>Prev</button>
                <div className={`px-4 py-2 border rounded-lg ${isDark ? "border-gray-600 text-gray-300" : "border-gray-300 text-gray-700"}`}>Page {currentPage} / {totalPages}</div>
                <button onClick={() => paginate(currentPage + 1)} disabled={currentPage === totalPages || isBusy} type="button" className={`px-4 py-2 border rounded-lg font-semibold ${isDark ? "border-gray-600 text-gray-300 hover:bg-gray-700" : "border-gray-300 text-gray-700 hover:bg-gray-100"} ${currentPage === totalPages || isBusy ? "opacity-50 cursor-not-allowed" : ""}`}>Next</button>
              </div>
            </div>
          )}

          <ToastContainer position="top-right" autoClose={3000} closeOnClick pauseOnHover theme={isDark ? "dark" : "light"} />
        </div>
      </div>
    </VehicleLayoutWrapper>
  );
};

export default TyreMaintenanceCostReport;